"use client";

import { type IOrderDeliveryAddressComponents } from "@order-up/models";

/**
 * Reusable structured delivery-address form. Edits the canonical
 * `IOrderDeliveryAddressComponents` shape directly (no parallel type), so the
 * same value flows straight to the cart → order → DoorDash Drive quote/dispatch
 * without re-parsing. Used by the order-config modal (and reusable at checkout).
 */
export interface DeliveryAddressFormProps {
  value: IOrderDeliveryAddressComponents;
  onChange: (next: IOrderDeliveryAddressComponents) => void;
  disabled?: boolean;
}

/** True once the address has the minimum a courier needs (street + city + state + zip). */
export function isDeliveryAddressComplete(
  a?: IOrderDeliveryAddressComponents,
): boolean {
  return !!(
    a &&
    a.line1?.trim() &&
    a.city?.trim() &&
    a.state?.trim() &&
    a.postalCode?.trim()
  );
}

/** Human-readable one-line label, e.g. "123 Main St, Austin, TX 78701". */
export function formatDeliveryAddress(a?: IOrderDeliveryAddressComponents): string {
  if (!a) return "";
  const street = [a.line1, a.line2].filter(Boolean).join(", ");
  const cityState = [a.city, [a.state, a.postalCode].filter(Boolean).join(" ").trim()]
    .filter(Boolean)
    .join(", ");
  return [street, cityState].filter(Boolean).join(", ");
}

const FIELD_CLASS =
  "w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[var(--menu-primary,#dc2626)] focus:border-transparent disabled:bg-gray-50 disabled:text-gray-400";

export default function DeliveryAddressForm({
  value,
  onChange,
  disabled,
}: DeliveryAddressFormProps) {
  const set = (patch: Partial<IOrderDeliveryAddressComponents>) =>
    onChange({ ...value, ...patch });

  return (
    <div className="space-y-3">
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          Street address
        </label>
        <input
          className={FIELD_CLASS}
          value={value.line1 ?? ""}
          disabled={disabled}
          placeholder="123 Main St"
          autoComplete="address-line1"
          onChange={(e) => set({ line1: e.target.value })}
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          Apt / suite <span className="text-gray-400">(optional)</span>
        </label>
        <input
          className={FIELD_CLASS}
          value={value.line2 ?? ""}
          disabled={disabled}
          placeholder="Apt 4B"
          autoComplete="address-line2"
          onChange={(e) => set({ line2: e.target.value })}
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">City</label>
          <input
            className={FIELD_CLASS}
            value={value.city ?? ""}
            disabled={disabled}
            placeholder="Austin"
            autoComplete="address-level2"
            onChange={(e) => set({ city: e.target.value })}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">State</label>
            <input
              className={FIELD_CLASS}
              value={value.state ?? ""}
              disabled={disabled}
              placeholder="TX"
              maxLength={2}
              autoComplete="address-level1"
              onChange={(e) => set({ state: e.target.value.toUpperCase() })}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">ZIP</label>
            <input
              className={FIELD_CLASS}
              value={value.postalCode ?? ""}
              disabled={disabled}
              placeholder="78701"
              inputMode="numeric"
              autoComplete="postal-code"
              onChange={(e) => set({ postalCode: e.target.value })}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
